import '@fontsource-variable/nunito';
import { registerSW } from 'virtual:pwa-register';
import { Component, type ReactNode, useEffect, useState, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';

import App from './App';
import { Button } from './components/ui';
import { desktop } from './lib/desktop';
import { workspace } from './lib/engine';
import './styles.css';
import './design-system.css';
import './ui.css';

class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? (
      <main className="fatal-error">
        <h1>Cairn couldn’t render this page.</h1>
        <p>Your stored data has not been removed. Reload to retry.</p>
        <Button variant="ghost" onClick={() => location.reload()}>
          Reload
        </Button>
      </main>
    ) : (
      this.props.children
    );
  }
}

function UpdateNotice() {
  const [apply, setApply] = useState<(reload?: boolean) => Promise<void>>();
  const state = useSyncExternalStore(workspace.subscribe, workspace.snapshot);

  useEffect(() => {
    const update = registerSW({
      onNeedRefresh() {
        setApply(() => update);
      },
    });
  }, []);

  return apply ? (
    <aside className="update-notice" role="status">
      <span>
        An update is ready. Save any open form before reloading. Locally saved pending edits are
        retained.
      </span>
      <Button variant="primary" disabled={Boolean(state.saving)} onClick={() => void apply(true)}>
        Reload to update
      </Button>
    </aside>
  ) : null;
}

// The Mac app draws its own title bar and updates itself rather than through a
// service worker, which its app:// scheme cannot register (docs/desktop-app.md).
if (desktop) {
  document.documentElement.dataset.shell = 'desktop';
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <App />
    {!desktop && <UpdateNotice />}
  </ErrorBoundary>,
);
